import { useEffect, useState } from "react";
import axiosInstance from "../../../../services/AxiosInstance";
import Api from "../../../../constants/Api";
import { PanelLoading } from "../../../components/Common/LoadingPlaceholder";
import { ApiError } from "../../../components/Common/ApiError";
import { PerformancePanel } from "./common/PerformanceWorkspace";
import { formatCurrency, formatPercent, splitTicketGroups } from "./common/performanceFormat";
import "./CategoryTicketPanel.css";

/**
 * Bloco de um grupo: o ticket da equipe em destaque e, embaixo, em quantas comandas o grupo
 * apareceu, com uma barra de 0 a 100% para a leitura ser imediata.
 *
 * @param {{group: {key: string, label: string, team: object}}} props
 */
const CategoryTicketTile = ({ group }) => (
    <div className="category-ticket__tile">
        <span className="category-ticket__label" title={group.label}>{group.label}</span>
        <strong className="category-ticket__value">{formatCurrency(group.team.ticket)}</strong>
        <small>por atendimento</small>
        <div className="category-ticket__track" aria-hidden="true">
            <span style={{ width: `${Math.min(100, Number(group.team.share) || 0)}%` }} />
        </div>
        <small>Em {formatPercent(group.team.share)} das comandas</small>
        <small>{formatCurrency(group.team.revenue)} no período</small>
    </div>
);

/**
 * Quadro "Ticket por categoria" do Desempenho, só com a equipe.
 *
 * Mostra quanto cada atendimento da equipe gera, em média, com serviços, com produtos e com cada
 * categoria. O valor de cada colaborador fica na análise individual: a lista pessoa a pessoa aqui
 * poluía o painel. A conta vem pronta do backend (CategoryTicketService), a mesma dos indicadores
 * de ticket dos planos.
 *
 * @param {{scope?: {startDate: string, endDate: string, branchId?: number}}} props recorte da última
 *        pesquisa do painel. Um objeto novo a cada pesquisa recarrega o quadro, mesmo com o mesmo mês.
 */
const CategoryTicketPanel = ({ scope }) => {
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!scope?.startDate || !scope?.endDate) {
            return undefined;
        }
        // Troca rápida de mês ou filial não pode deixar a resposta antiga sobrescrever a nova.
        let active = true;
        setLoading(true);
        axiosInstance.get(Api.performance.categoryTickets, {
            params: { startDate: scope.startDate, endDate: scope.endDate, branchId: scope.branchId || undefined }
        })
            .then(({ data }) => {
                if (active) setGroups(data?.groups || []);
            })
            .catch((error) => {
                if (!active) return;
                setGroups([]);
                ApiError(error);
            })
            .finally(() => {
                if (active) setLoading(false);
            });
        return () => {
            active = false;
        };
    }, [scope]);

    // Sem atendimento no período não há o que mostrar; o painel some em vez de exibir zeros.
    if (!loading && groups.length === 0) {
        return null;
    }

    const { types, categories } = splitTicketGroups(groups);

    return (
        <PerformancePanel
            title="Ticket por categoria"
            subtitle="Quanto cada atendimento da equipe gera, em média, em cada categoria. O valor de cada colaborador está em Ver análise."
            className="mb-4"
        >
            {loading ? (
                <PanelLoading height={220} label="Calculando o ticket por categoria..." />
            ) : (
                <>
                    <div className="category-ticket__grid">
                        {types.map((group) => <CategoryTicketTile key={group.key} group={group} />)}
                    </div>
                    {categories.length > 0 && (
                        <>
                            <h6 className="category-ticket__heading">Por categoria</h6>
                            <div className="category-ticket__grid">
                                {categories.map((group) => <CategoryTicketTile key={group.key} group={group} />)}
                            </div>
                        </>
                    )}
                </>
            )}
        </PerformancePanel>
    );
};

export default CategoryTicketPanel;
